import React, { useState, useEffect, useRef } from 'react';
import { fabric } from 'fabric';
import { Toolbar } from './components/Toolbar';
import { ColorPicker } from './components/ColorPicker';
import { TextPanel } from './components/TextPanel';
import { PhotoPanel } from './components/PhotoPanel';
import { Canvas } from './components/Canvas';
import { useStore } from './store';
import { exportToPNG, exportToPDF } from './utils/export';

interface AppProps {
  containerWidth?: number;
  containerHeight?: number;
  canvasWidth?: number;
  canvasHeight?: number;
  canvasLeft?: number;
  canvasTop?: number;
  allowedBackgroundColors?: string[];
  allowedTextColors?: string[];
  allowedFonts?: Array<{ name: string; value: string }>;
  allowCustomTextColors?: boolean;
  allowCustomBackgroundColors?: boolean;
  objectBackground?: string;
  transparentBackground?: boolean;
  useWooCommerceButton?: boolean;
  hideExportButtons?: boolean;
  productId?: string;
}

export default function App({
  containerWidth = 500,
  containerHeight = 500,
  canvasWidth = 108,
  canvasHeight = 160,
  canvasLeft = 196,
  canvasTop = 190,
  allowedBackgroundColors = [
    '#FFFFFF', '#E5E7EB', '#6B7280', '#000000',
    '#3B82F6', '#60A5FA', '#10B981', '#F87171'
  ],
  allowedTextColors = [
    '#000000', '#FFFFFF', '#EF4444', '#F59E0B',
    '#10B981', '#3B82F6', '#6366F1', '#8B5CF6',
    '#EC4899', '#6B7280', '#78350F', '#064E3B'
  ],
  allowedFonts,
  allowCustomTextColors = true,
  allowCustomBackgroundColors = true,
  objectBackground,
  transparentBackground = false,
  useWooCommerceButton = true,
  hideExportButtons = false,
  productId
}: AppProps) {
  const [activeTab, setActiveTab] = useState('text-initial-text');
  const { backgroundColor, setBackgroundColor, addTextElement, addImageElement, textElements, imageElements, activeTextId, setActiveTextId, exportDesign } = useStore();
  const canvasRef = useRef<fabric.Canvas | null>(null);

  useEffect(() => {
    if (activeTextId) {
      setActiveTab(`text-${activeTextId}`);
    }
  }, [activeTextId]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab.startsWith('text-')) {
      const textId = tab.replace('text-', '');
      setActiveTextId(textId);
    } else {
      setActiveTextId(null);
    }
  };

  const handleElementSelect = (type: string, id: string) => {
    if (type === 'text') {
      setActiveTab(`text-${id}`);
      setActiveTextId(id);
    } else if (type === 'photo') {
      setActiveTab(`photo-${id}`);
    }
  };

  const handleAddText = () => {
    const id = addTextElement();
    setActiveTab(`text-${id}`);
  };

  const handleAddPhoto = () => {
    setActiveTab('photo');
  };

  const handleAddToCart = async () => {
    if (!canvasRef.current || !productId) return;

    try {
      const design = exportDesign();
      const pngData = await exportToPNG({
        canvas: canvasRef.current,
        containerWidth,
        containerHeight,
        canvasWidth,
        canvasHeight,
        canvasLeft,
        canvasTop,
        objectBackground
      });
      
      const pdfData = await exportToPDF({
        canvas: canvasRef.current,
        containerWidth,
        containerHeight,
        canvasWidth,
        canvasHeight,
        canvasLeft,
        canvasTop
      });

      const formData = new FormData();
      formData.append('action', 'customizer_add_to_cart');
      formData.append('security', (window as any).wc_add_to_cart_params?.nonce || '');
      formData.append('product_id', productId);
      formData.append('design', JSON.stringify(design));
      formData.append('png_data', pngData);
      formData.append('pdf_data', btoa(String.fromCharCode(...new Uint8Array(await pdfData.arrayBuffer()))));

      const response = await fetch(
        (window as any).wc_add_to_cart_params?.ajax_url || '/wp-admin/admin-ajax.php',
        {
          method: 'POST',
          body: formData,
          credentials: 'same-origin'
        }
      );

      const result = await response.json();
      if (result.success) {
        // Refresh cart fragments
        if ((window as any).wc_add_to_cart_params?.cart_url) {
          window.location.href = (window as any).wc_add_to_cart_params.cart_url;
        }
      } else {
        console.error('Failed to add to cart:', result);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const renderPanel = () => {
    if (activeTab === 'background' && !transparentBackground) {
      return (
        <div>
          <h2 className="text-lg font-semibold mb-4">Couleur de fond</h2>
          <ColorPicker
            colors={allowedBackgroundColors}
            selectedColor={backgroundColor}
            onColorSelect={setBackgroundColor}
            allowCustom={allowCustomBackgroundColors}
            type="background"
          />
        </div>
      );
    }

    if (activeTab === 'add') {
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold mb-4">Ajouter</h2>
          <button
            onClick={handleAddText}
            className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            Ajouter du texte
          </button>
          <button
            onClick={handleAddPhoto}
            className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            Ajouter une photo
          </button>
        </div>
      );
    }

    if (activeTab.startsWith('text-')) {
      return (
        <div>
          <h2 className="text-lg font-semibold mb-4">Texte</h2>
          <TextPanel
            colors={allowedTextColors}
            allowCustomColors={allowCustomTextColors}
            allowedFonts={allowedFonts}
            textId={activeTab.replace('text-', '')}
          />
        </div>
      );
    }

    if (activeTab === 'photo' || activeTab.startsWith('photo-')) {
      const imageId = activeTab === 'photo' ? undefined : activeTab.replace('photo-', '');
      return (
        <div>
          <h2 className="text-lg font-semibold mb-4">Photo</h2>
          <PhotoPanel imageId={imageId} />
        </div>
      );
    }

    return null;
  };

  const setCanvasRef = (canvas: fabric.Canvas | null) => {
    canvasRef.current = canvas;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-lg overflow-hidden">
        <Toolbar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          textElements={textElements}
          imageElements={imageElements}
          transparentBackground={transparentBackground}
          canvasRef={canvasRef}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          objectBackground={objectBackground}
          hideExportButtons={hideExportButtons}
        />
        
        <div className="grid grid-cols-2 gap-8 p-8">
          <div className="space-y-6">
            {renderPanel()}
          </div>
          
          <div className="relative">
            <Canvas 
              onElementSelect={handleElementSelect}
              containerWidth={containerWidth}
              containerHeight={containerHeight}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              canvasLeft={canvasLeft}
              canvasTop={canvasTop}
              objectBackground={objectBackground}
              onCanvasReady={setCanvasRef}
            />
          </div>
        </div>
        
        {!useWooCommerceButton && (
          <div className="p-4 border-t border-gray-200">
            <button 
              onClick={handleAddToCart}
              className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              Ajouter au panier
            </button>
          </div>
        )}
      </div>
    </div>
  );
}